// client/src/navigation/freshNavigation.ts

/**
 * True when this page load is a brand-new top-level navigation (typed URL,
 * external/bookmark link, reopened tab) rather than a same-tab reload or an
 * in-app back/forward that landed on a history entry `useViewBackStack`
 * previously wrote to.
 *
 * Both `useViewBackStack` (`{ navType: 'view', view }`) and
 * `useModalBackDismiss`/`popstateDispatcher` (`{ navType: 'modal', modalId }`)
 * stamp every entry they control via `history.replaceState`/`pushState`. A
 * reload preserves whichever marker is on the current entry (browsers keep
 * `history.state` across same-document reloads) — including a modal entry,
 * if one was open at reload time — and back/forward within the app lands on
 * an entry we stamped ourselves. All of those read as "not fresh" here: any
 * recognized `navType` means this entry is one our own history code wrote,
 * not one the browser created for a brand-new navigation. A brand-new
 * navigation (the browser assigns a fresh entry with `state: null`) reads as
 * fresh.
 *
 * Must be evaluated at module load time, before `useViewBackStack`'s mount
 * effect runs its `replaceState` call for the current entry — after that,
 * every entry (fresh or not) would carry a marker and this signal would be
 * lost. Importing this module for its side-effect-free top-level constant
 * from `index.tsx`, ahead of `App`, guarantees that ordering.
 *
 * Known tradeoff: a full-page Azure AD redirect round-trip (forced
 * re-auth) also lands here as a fresh `navigate` with `state: null`, so it
 * reads as "fresh" too, even though the user never left the tab. Accepted as
 * a rare edge case — see `clearStaleSessionState.ts`.
 */
const KNOWN_NAV_TYPES = new Set(['view', 'modal']);
const state = window.history.state as { navType?: string } | null;
export const isFreshTopLevelNavigation = !(
  state && KNOWN_NAV_TYPES.has(state.navType ?? '')
);
